import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { FileText, Loader2, Download, Sparkles } from 'lucide-react';

const prevMonthKey = () => {
  const d = new Date();
  d.setDate(1); d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const thisMonthKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const ReportsDialog = ({ open, onOpenChange }) => {
  const { API, getAuthHeaders } = useAuth();
  const base = API.replace('/api', '');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(null);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ai-social/reports`, getAuthHeaders());
      setReports(res.data);
    } catch { toast.error('Errore nel caricamento dei report'); }
    finally { setLoading(false); }
  }, [API, getAuthHeaders]);

  useEffect(() => { if (open) fetchReports(); }, [open, fetchReports]);

  const generate = async (month) => {
    setGenerating(month);
    try {
      await axios.post(`${API}/ai-social/reports/generate?month=${month}&force=true`, {}, getAuthHeaders());
      toast.success('Report generato');
      fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella generazione del report');
    } finally { setGenerating(null); }
  };

  const has = (m) => reports.some(r => r.month === m);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="reports-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2"><FileText className="w-5 h-5 text-primary" /> Report mensili</DialogTitle>
          <DialogDescription>Ogni 1° del mese ricevi via WhatsApp ed email il PDF con i risultati e i consigli dello Strategist per il mese successivo.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          {!has(prevMonthKey()) && (
            <Button size="sm" variant="outline" onClick={() => generate(prevMonthKey())} disabled={!!generating} data-testid="report-generate-prev-btn">
              {generating === prevMonthKey() ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />} Genera report mese scorso
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => generate(thisMonthKey())} disabled={!!generating} data-testid="report-generate-current-btn">
            {generating === thisMonthKey() ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />} Anteprima mese in corso
          </Button>
        </div>
        {loading ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div> : reports.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4" data-testid="reports-empty">Nessun report ancora. Il primo arriverà automaticamente il 1° del prossimo mese.</p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="reports-list">
            {reports.map(r => (
              <div key={r.month} className="flex items-center gap-3 rounded-lg border p-2.5" data-testid={`report-row-${r.month}`}>
                <FileText className="w-5 h-5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{r.month_label}</p>
                  <p className="text-xs text-muted-foreground">{r.kpis?.published ?? 0} post · {r.kpis?.engagement ?? 0} interazioni · {r.kpis?.ai_posts ?? 0} con AI</p>
                </div>
                {r.sent && <Badge variant="outline" className="text-[10px]">Inviato</Badge>}
                <a href={`${base}${r.url}`} target="_blank" rel="noreferrer" data-testid={`report-download-${r.month}`}>
                  <Button size="sm" variant="secondary"><Download className="w-3.5 h-3.5 mr-1" /> PDF</Button>
                </a>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReportsDialog;
